import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useGlobalization } from '@Translations/useGlobalization';

export interface StartOptionCardProps {
	icon: React.ReactNode;
	iconBg: string;
	title: React.ReactNode;
	body: React.ReactNode;
	selected: boolean;
	onSelect: () => void;
	eyebrow?: string;
	badge?: string;
	disabled?: boolean;
	onInfo?: () => void;
}

export const StartOptionCard = ({
	icon,
	iconBg,
	title,
	body,
	selected,
	onSelect,
	eyebrow,
	badge,
	disabled,
	onInfo,
}: StartOptionCardProps) => {
	const { t } = useGlobalization();
	const select = () => {
		if (!disabled) onSelect();
	};
	return (
		<Box
			role="button"
			tabIndex={disabled ? -1 : 0}
			aria-pressed={selected}
			aria-disabled={disabled}
			onClick={select}
			onKeyDown={e => {
				// Only the card itself toggles selection — not the inner info icon.
				if (e.target !== e.currentTarget) return;
				if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
					e.preventDefault();
					select();
				}
			}}
			sx={{
				width: '100%',
				display: 'flex',
				gap: 1.5,
				p: 2,
				borderRadius: '4px',
				background: '#fff',
				cursor: disabled ? 'default' : 'pointer',
				border: selected ? '2px solid #2a7de1' : '1px solid #e3e7ed',
				boxShadow: selected
					? '0 1px 2px rgba(16,24,40,.06), 0 8px 24px rgba(16,24,40,.08)'
					: 'none',
				opacity: disabled ? 0.6 : 1,
				transition: 'border-color .15s, box-shadow .15s',
				'&:focus-visible': {
					outline: '2px solid #2a7de1',
					outlineOffset: '2px',
				},
			}}>
			<Box
				sx={{
					flex: 'none',
					width: 36,
					height: 36,
					borderRadius: '4px',
					background: iconBg,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}>
				{icon}
			</Box>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'flex-start',
						justifyContent: 'space-between',
						gap: 1,
						mb: 0.5,
					}}>
					<Box>
						{eyebrow && (
							<Typography
								sx={{
									fontSize: 11,
									fontWeight: 800,
									letterSpacing: '0.06em',
									color: '#2a7de1',
									mb: 0.25,
								}}>
								{eyebrow}
							</Typography>
						)}
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1,
							}}>
							<Typography
								sx={{
									fontSize: 15,
									fontWeight: 700,
									color: '#0d1520',
								}}>
								{title}
							</Typography>
							{badge && (
								<Box
									component="span"
									sx={{
										fontSize: 11,
										fontWeight: 700,
										color: '#5b6472',
										background: '#eef1f4',
										borderRadius: '9999px',
										px: 1,
										py: '2px',
									}}>
									{badge}
								</Box>
							)}
						</Box>
					</Box>
					{onInfo && (
						<IconButton
							size="small"
							aria-label={t('Learn more')}
							onClick={e => {
								e.stopPropagation();
								onInfo();
							}}
							sx={{ mt: '-4px', color: '#97a0ac' }}>
							<InfoOutlined fontSize="small" />
						</IconButton>
					)}
				</Box>
				{body}
			</Box>
		</Box>
	);
};
